import React from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Chip, Slider, Stack, Typography } from '@mui/material';
import type { Book } from '../../books/types';
import {
  clampBookPosition,
  formatBookDisplay,
  formatBookProgressLabel,
  formatProgressDelta,
} from '../../books/utils';

interface BookProgressSliderProps {
  book: Book;
  onCommit: (position: number) => void;
}

const BookProgressSlider: React.FC<BookProgressSliderProps> = ({ book, onCommit }) => {
  const [previewPosition, setPreviewPosition] = React.useState<number | null>(null);

  const livePosition = previewPosition ?? book.current_position;
  const delta = livePosition - book.current_position;
  const introWidth = `${book.intro_cutoff_pct}%`;

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatBookProgressLabel(book, livePosition)}
        </Typography>
        {previewPosition !== null ? (
          <Chip
            size="small"
            color={delta >= 0 ? 'success' : 'warning'}
            label={formatProgressDelta(delta)}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Target {book.expected_display === 'Start' ? 'Start' : `p.${book.expected_display}`}
          </Typography>
        )}
      </Stack>

      <Box sx={{ position: 'relative', height: 38, pt: 2.25 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 18,
            bottom: 0,
            borderRadius: 999,
            bgcolor: 'action.disabledBackground',
            overflow: 'hidden',
          }}
        >
          {book.intro_pages > 0 ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: introWidth,
                bgcolor: alpha(book.color, 0.18),
              }}
            />
          ) : null}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: `${book.expected_pct}%`,
              bgcolor: 'grey.500',
              opacity: 0.35,
            }}
          />
          <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: `${(livePosition / Math.max(book.total_positions, 1)) * 100}%`,
                bgcolor: book.color,
              }}
            />
        </Box>
        {book.intro_pages > 0 ? (
          <>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 2,
                left: introWidth,
                transform: 'translateX(-50%)',
                color: 'text.secondary',
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              intro
            </Typography>
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                bottom: -2,
                left: introWidth,
                width: 2,
                bgcolor: 'grey.500',
                borderRadius: 999,
              }}
            />
          </>
        ) : null}
      </Box>

      <Slider
        value={livePosition}
        min={0}
        max={Math.max(book.total_positions, 1)}
        step={1}
        valueLabelDisplay="off"
        onChange={(_event, next) => {
          const resolved = Array.isArray(next) ? next[0] : next;
          setPreviewPosition(clampBookPosition(book, resolved));
        }}
        onChangeCommitted={(_event, next) => {
          const resolved = clampBookPosition(book, Array.isArray(next) ? next[0] : next);
          setPreviewPosition(null);
          if (resolved !== book.current_position) {
            onCommit(resolved);
          }
        }}
        sx={{
          color: book.color,
          '& .MuiSlider-thumb': {
            boxShadow: `0 0 0 4px ${alpha(book.color, 0.16)}`,
          },
        }}
      />

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          Start
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {book.intro_pages > 0 ? `Intro ends at ${formatBookDisplay(book, book.intro_pages)}` : 'No intro section'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Done
        </Typography>
      </Stack>
    </Stack>
  );
};

export default BookProgressSlider;
